import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WHATSAPP } from '../data/contacto';

const PHASES = [
  {
    num: '01',
    name: 'Entendemos bien la idea',
    when: '48 horas',
    color: '#29D880',
    items: [
      'Qué construir primero para validar que funciona',
      'Cuánto tiempo y plata necesitás',
      'Cómo evitar gastar de más en esta etapa',
      'Un plan concreto para avanzar sin perderse',
    ],
  },
  {
    num: '02',
    name: 'Construimos la primera versión',
    when: '3 a 5 semanas',
    color: '#6C63FF',
    items: [
      'Las pantallas principales que van a usar tus clientes',
      'Todo funciona de verdad, no es una maqueta',
      'Tu gente puede probarlo y darte feedback',
      'Disponible para mostrárselo a quien quieras',
    ],
  },
  {
    num: '03',
    name: 'La lanzamos.',
    when: '1 semana',
    color: '#F59E0B',
    items: [
      'La probamos con usuarios reales antes de lanzar',
      'Ajustamos lo que haga falta según lo que digan',
      'Miramos que todo ande bien en todo momento',
      'Definimos cómo crecer a partir de ahí',
    ],
  },
];

const useReveal = (delay = 0) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return visible;
};

const AppRoadmap = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const idea   = state?.idea   || 'Tu idea';
  const timing = state?.timing || 'Cuanto antes mejor';

  // Personalizar el plan con la idea del usuario
  const ideaShort = idea.length > 40 ? idea.slice(0, 40).trim() + '...' : idea;
  const PHASES_PERSONAL = [
    {
      ...PHASES[0],
      items: [
        `Qué construir primero de "${ideaShort}" para validar que funciona`,
        'Cuánto tiempo y plata necesitás realmente',
        'Cómo evitar gastar de más en esta etapa',
        'Un plan concreto para avanzar sin perderse',
      ],
    },
    PHASES[1],
    PHASES[2],
  ];

  const header = useReveal(0);
  const phases = useReveal(400);
  const cta    = useReveal(900);

  return (
    <div className="guia-bg min-h-screen font-sans text-gray-900">

      <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
        <span className="text-gray-800 font-semibold tracking-[0.2em] text-sm">GuIA</span>
        <button onClick={() => navigate('/')} className="text-gray-300 text-xs hover:text-gray-600 transition-colors">
          ← nueva consulta
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">

        {/* Header */}
        <div
          className="mb-10 transition-all duration-700"
          style={{ opacity: header ? 1 : 0, transform: header ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <p className="text-gray-400 text-xs tracking-[0.4em] uppercase mb-4">El plan.</p>
          <div className="rounded-2xl border border-guia-mint/15 bg-guia-mint/[0.04] px-6 py-5 mb-4">
            <p className="text-gray-400 text-xs mb-2">La idea</p>
            <p className="text-gray-900 text-xl font-light leading-snug">"{idea}"</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="rounded-xl border border-black/[0.08] bg-black/[0.02] px-4 py-2.5 text-xs text-gray-400">
              ¿Cuándo? <span className="text-gray-700">{timing}</span>
            </div>
            <div className="rounded-xl border border-guia-mint/20 bg-guia-mint/[0.05] px-4 py-2.5 text-xs text-guia-mint">
              Plan completo en 48hs
            </div>
          </div>
        </div>

        {/* Phases */}
        <div
          className="mb-10 transition-all duration-700 space-y-3"
          style={{ opacity: phases ? 1 : 0, transform: phases ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <p className="text-gray-400 text-xs tracking-[0.4em] uppercase mb-4">Cómo arrancamos.</p>
          {PHASES_PERSONAL.map((ph, i) => (
            <div
              key={i}
              className="rounded-2xl border bg-black/[0.02] overflow-hidden"
              style={{ borderColor: `${ph.color}25` }}
            >
              <div
                className="px-5 py-5"
                style={{ borderLeft: `3px solid ${ph.color}60` }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: `${ph.color}20`, color: ph.color }}
                  >
                    {ph.when}
                  </span>
                  <span className="text-gray-700 font-medium text-sm">{ph.name}</span>
                </div>
                <div className="space-y-1.5">
                  {ph.items.map((item, j) => (
                    <div key={j} className="flex items-start gap-2.5 text-gray-400 text-xs leading-relaxed">
                      <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: `${ph.color}60` }} />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className="transition-all duration-700"
          style={{ opacity: cta ? 1 : 0, transform: cta ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <div className="rounded-2xl border border-guia-mint/20 bg-guia-mint/[0.04] px-6 py-7 text-center">
            <p className="text-gray-700 text-base mb-1 font-light">
              Primer paso: entender la idea.
            </p>
            <p className="text-gray-400 text-sm mb-1 leading-relaxed">
              En 48hs te decimos qué construir, cómo y cuánto cuesta. Sin vueltas.
            </p>
            <p className="text-gray-300 text-xs mb-6">El diagnóstico es sin costo.</p>
            <div className="flex justify-center gap-3 flex-wrap">
              <a
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hola GuIA, quiero arrancar con mi idea: ${idea}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-7 py-3.5 rounded-full bg-guia-mint text-black text-sm font-semibold hover:bg-guia-mint/90 transition-all hover:scale-[1.02]"
                style={{ boxShadow: '0 0 32px rgba(41,216,128,0.2)' }}
              >
                Hablar por WhatsApp →
              </a>
              <button
                onClick={() => navigate('/')}
                className="px-7 py-3.5 rounded-full border border-black/[0.08] text-gray-500 text-sm hover:border-black/20 hover:text-gray-600 transition-all"
              >
                Nueva consulta
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AppRoadmap;
